// ** react imports
import { useState } from 'react';

// ** router imports
import { Link, useNavigate } from 'react-router-dom';

// ** redux imports
import { useDispatch } from 'react-redux';
import { setToken, setUserDetails } from '../redux/slices/users';

// ** validation imports
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// ** hook imports
import { useLoginUser } from '../hooks/apis/useUsers';

// ** toast imports
import { toast } from 'react-toastify';

// ** image imports
import AuthImage from '../images/auth-image.jpg';
import Logo from '../images/Logo.png';

// ** components imports
import LoadingButton from '../components/common/Buttons/LoadingButton';
import NormalInput from '../components/common/Inputs/NormalInput';
import PasswordInput from '../components/common/Inputs/PasswordInput';

// ** views imports
import VerifyOtpForm from '../views/users/VerifyOTP';

// ** enums imports
import { BUTTON_THEMES, USER_ROLES } from '../enums/common';

function Signin(props) {
  // props
  const {} = props;

  // state
  const [data, setData] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // custom hooks
  const { isLoginUserLoading, loginUser } = useLoginUser();

  // validation schema
  const validationSchema = yup.object().shape({
    email: yup.string().email('Invalid email').required('Email is required'),
    password: yup
      .string()
      .required('Password is required')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[a-zA-Z\d!@#$%^&*]{8,}$/,
        'Password at least 8 characters, one special character, one uppercase, one lowercase, and one number'
      ),
  });

  // react hook form
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  // form submit handler
  const onSubmit = async data => {
    const response = await loginUser(data);

    if (response?.data?.success) {
      const { user } = response.data.data || {};

      setData(user);
      setIsLoggedIn(true);
    }
  };

  // render
  const LoginForm = () => (
    <div className="max-w-sm mx-auto w-full px-4 py-8">
      <h1 className="text-3xl text-gray-800 dark:text-gray-100 font-bold mb-6">
        Welcome back!
      </h1>
      {/* Form */}
      <form method="POST" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <div>
            <NormalInput
              label="Email Address*"
              type="email"
              name="email"
              register={register}
              errors={errors.email?.message}
            />
          </div>

          <div>
            <PasswordInput
              label="Password*"
              name="password"
              register={register}
              errors={errors.password?.message}
            />
          </div>
        </div>
        <div className="flex items-center justify-between mt-6">
          <div className="mr-1">
            <Link
              className="text-sm underline hover:no-underline"
              to="/auth/forgot-password"
            >
              Forgot Password?
            </Link>
          </div>
          <LoadingButton
            type="submit"
            theme={BUTTON_THEMES.PRIMARY}
            isLoading={isLoginUserLoading}
          >
            Sign in
          </LoadingButton>
        </div>
      </form>{' '}
    </div>
  );

  return (
    <main className="bg-white dark:bg-gray-900">
      <div className="relative md:flex">
        {!isLoggedIn && (
          <div className="md:w-1/2">
            <div className="min-h-dvh h-full flex flex-col after:flex-1">
              <div className="flex-1">
                <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
                  <Link className="block" to="/">
                    <div className="w-10 h-10">
                      <img
                        src={Logo}
                        alt="Multi Tax"
                        className="h-full w-full object-contain"
                      />
                    </div>
                  </Link>
                </div>
              </div>
              <LoginForm />
            </div>
          </div>
        )}
        {isLoggedIn && <VerifyOtpForm isLoginVerification data={data} />}

        <div
          className="hidden md:block absolute top-0 bottom-0 right-0 md:w-1/2"
          aria-hidden="true"
        >
          <img
            className="object-cover object-center w-full h-full"
            src={AuthImage}
            width="760"
            height="1024"
            alt="Authentication"
          />
        </div>
      </div>
    </main>
  );
}

export default Signin;
